import {
    ExceptionRateMode,
    ExceptionRateRule,
    ExceptionSaveFormat,
    ExceptionSettlementMode,
    PutCoachExceptionRatesBody,
    PutCoachExceptionRatesRequest,
} from './types';

/**
 * 예외 수업료 편집 행. 값은 입력 중 빈칸을 허용하려고 number|null 로 둔다.
 * target: 'reRegistration' 또는 'category:<key>'.
 *
 * reRegistrationValue·noshowValue 는 기본 정산 전용 값이다. 상세·세션별 정산 화면에서는 보이지 않지만
 * 편집 행에 그대로 들고 있다가 저장 때 되돌려 보낸다(배열 통째 교체라 빠뜨리면 지워진다).
 */
export interface ExceptionRuleDraft {
    target: string;
    mode: ExceptionRateMode;
    value: number | null;
    reRegistrationValue: number | null;
    noshowValue: number | null;
}

export const ruleTargetOf = (rule: ExceptionRateRule): string =>
    rule.match.type === 'category' ? `category:${rule.match.key}` : 'reRegistration';

const finiteOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

export const toDrafts = (rules: ExceptionRateRule[] | undefined): ExceptionRuleDraft[] =>
    (rules ?? []).map((rule) => ({
        target: ruleTargetOf(rule),
        mode: rule.mode,
        value: rule.value,
        reRegistrationValue: finiteOrNull(rule.reRegistrationValue),
        noshowValue: finiteOrNull(rule.noshowValue),
    }));

/**
 * 이 정산 방식 화면에서 숨기는 규칙. 숨긴 규칙도 저장 때는 그대로 되돌려 보낸다 — 다른 방식으로 바꾸면 다시 쓰인다.
 * - 기본 정산은 「재등록 수강권 전체」 예외를 계산하지 않는다(서버 P3).
 * - 「시간 상관없이 IN 요율」(inMultiplier)은 상세 정산에서만 계산된다 — 기본·세션별 정산 화면에서는 숨긴다.
 */
export const isHiddenInMode = (
    rule: Pick<ExceptionRateRule, 'match'> & { mode?: ExceptionRateMode },
    mode: ExceptionSettlementMode,
): boolean =>
    (mode === 'base' && rule.match.type === 'reRegistration') || (mode !== 'tier' && rule.mode === 'inMultiplier');

export const splitRulesForMode = (
    rules: ExceptionRateRule[] | undefined,
    mode: ExceptionSettlementMode,
): { visible: ExceptionRateRule[]; hidden: ExceptionRateRule[] } => {
    const visible: ExceptionRateRule[] = [];
    const hidden: ExceptionRateRule[] = [];
    (rules ?? []).forEach((rule) => (isHiddenInMode(rule, mode) ? hidden : visible).push(rule));
    return { visible, hidden };
};

/** 기본 정산의 고정·요율은 칸이 셋(신규·재등록·노쇼), 배수는 숫자 하나다. */
export const usesThreeValues = (mode: ExceptionSettlementMode, ruleMode: ExceptionRateMode): boolean =>
    mode === 'base' && (ruleMode === 'fixed' || ruleMode === 'percentage');

/** 숫자 하나로 곱하는 방식(배수·시간 상관없이 IN 배수) — 범위·자릿수가 같고 0 이 없다. */
const isMultiplierMode = (ruleMode: ExceptionRateMode) => ruleMode === 'multiplier' || ruleMode === 'inMultiplier';

export const MAX_FIXED_EXCEPTION_FEE = 9_999_999;
export const MAX_PERCENTAGE_EXCEPTION = 100;

/** 부동소수 허용 오차로 자릿수를 본다(서버와 같은 방식 — 45.55 * 100 = 4554.999…). */
const hasAtMostDecimals = (value: number, digits: number) => {
    const scale = 10 ** digits;
    return Math.abs(Math.round(value * scale) - value * scale) <= 1e-6;
};

const rangeMessage = (mode: ExceptionRateMode, subject: string): string | null => {
    if (mode === 'fixed') return `${subject} 금액은 0원 이상 정수로 넣어 주세요.`;
    if (mode === 'percentage') return `${subject} 요율은 0% 이상 100% 이하로, 소수 둘째 자리까지 넣어 주세요.`;
    return `${subject} 배수는 0보다 크고 10 이하로, 소수 넷째 자리까지 넣어 주세요.`;
};

const isValidValue = (mode: ExceptionRateMode, value: number): boolean => {
    if (!Number.isFinite(value)) return false;
    if (mode === 'fixed') return Number.isInteger(value) && value >= 0 && value <= MAX_FIXED_EXCEPTION_FEE;
    if (mode === 'percentage') return value >= 0 && value <= MAX_PERCENTAGE_EXCEPTION && hasAtMostDecimals(value, 2);
    return value > 0 && value <= 10 && hasAtMostDecimals(value, 4);
};

/**
 * 서버 검사와 같은 범위를 미리 본다(서버가 최종 판정). 실패면 사람이 읽을 문구, 통과면 null.
 * - 대상 미선택·중복 금지(이 화면에서 숨긴 규칙의 대상과도 겹치면 안 된다 — 저장 배열에 함께 실린다)
 * - fixed: 0 이상 정수(원) / percentage: 0~100, 소수 둘째 자리 / multiplier: 0 초과 10 이하, 소수 넷째 자리
 * - 기본 정산의 재등록·노쇼 칸: 비워도 된다. 넣었으면 신규 값과 같은 범위.
 */
export const validateExceptionDrafts = (
    drafts: ExceptionRuleDraft[],
    mode: ExceptionSettlementMode = 'tier',
    hiddenTargets: string[] = [],
): string | null => {
    const seen = new Set<string>();
    const hidden = new Set(hiddenTargets);
    for (let i = 0; i < drafts.length; i++) {
        const draft = drafts[i];
        const order = `${i + 1}번째 규칙`;
        if (!draft.target) return `${order}의 대상을 골라 주세요.`;
        if (seen.has(draft.target)) return `${order}의 대상이 위와 겹쳐요. 같은 대상은 한 번만 정할 수 있어요.`;
        if (hidden.has(draft.target)) {
            return `${order}의 대상은 상세 정산 전용 예외가 이미 있어요. 다른 수강권을 골라 주세요.`;
        }
        seen.add(draft.target);
        const threeValues = usesThreeValues(mode, draft.mode);
        if (draft.value === null || draft.value === undefined || Number.isNaN(draft.value)) {
            return threeValues ? `${order}의 신규 값을 넣어 주세요.` : `${order}의 값을 넣어 주세요.`;
        }
        if (!isValidValue(draft.mode, draft.value)) return rangeMessage(draft.mode, threeValues ? `${order}의 신규` : `${order}의`);
        if (threeValues) {
            if (draft.reRegistrationValue !== null && !isValidValue(draft.mode, draft.reRegistrationValue)) {
                return rangeMessage(draft.mode, `${order}의 재등록`);
            }
            if (draft.noshowValue !== null && !isValidValue(draft.mode, draft.noshowValue)) {
                return rangeMessage(draft.mode, `${order}의 노쇼`);
            }
        }
    }
    return null;
};

/** 기본 정산 전용 값을 실을 수 있는 규칙인가 — 서버 규칙(분류 조건의 고정·요율에만, 그 밖에 숫자면 400)과 같다. */
const canCarryExtras = (target: string, ruleMode: ExceptionRateMode) =>
    target.startsWith('category:') && !isMultiplierMode(ruleMode);

/**
 * 검사를 통과한 편집 행을 서버 형식으로. 재등록은 key: null.
 * 재등록·노쇼 두 키는 **항상** 싣는다(비우면 null) — 서버가 롤백돼 모르는 키면 400 으로 「저장 실패」가 된다.
 */
export const toRules = (drafts: ExceptionRuleDraft[]): ExceptionRateRule[] =>
    drafts.map((draft) => {
        const extras = canCarryExtras(draft.target, draft.mode);
        return {
            match: draft.target.startsWith('category:')
                ? { type: 'category' as const, key: draft.target.slice('category:'.length) }
                : { type: 'reRegistration' as const, key: null },
            mode: draft.mode,
            value: draft.value as number,
            reRegistrationValue: extras ? draft.reRegistrationValue ?? null : null,
            noshowValue: extras ? draft.noshowValue ?? null : null,
        };
    });

/**
 * 저장할 배열 = 화면에서 편집한 규칙 + 이 방식에서 숨긴 규칙(그대로). 숨긴 규칙은 뒤에 붙인다 —
 * 서버 오류의 규칙 번호(details.index)가 화면의 번호와 맞게.
 */
export const buildRulesToSave = (drafts: ExceptionRuleDraft[], hidden: ExceptionRateRule[]): ExceptionRateRule[] => [
    ...toRules(drafts),
    ...toRules(toDrafts(hidden)),
];

/** 저장된 규칙과 편집 행이 같은가(순서 포함, 기본 정산 전용 값 포함). */
export const isSameRules = (drafts: ExceptionRuleDraft[], rules: ExceptionRateRule[] | undefined): boolean =>
    JSON.stringify(drafts) === JSON.stringify(toDrafts(rules));

/** 0원·0% 가 들어간 칸이 있는가(저장 전에 한 번 더 확인한다). 숨은 값은 보지 않는다. */
export const hasZeroValue = (drafts: ExceptionRuleDraft[], mode: ExceptionSettlementMode): boolean =>
    drafts.some((draft) => {
        if (isMultiplierMode(draft.mode)) return false;
        if (draft.value === 0) return true;
        if (!usesThreeValues(mode, draft.mode)) return false;
        return draft.reRegistrationValue === 0 || draft.noshowValue === 0;
    });

/**
 * 저장하면 「항상 IN 요율의 배수」가 새로 걸리는 대상이 있는가 — 지금 저장된 규칙에서 그 대상이 이 방식이 아니었던 편집 행.
 * IN 요율이 0일 수 있는 강사에게 처음 걸 때 한 번 더 확인받는 데 쓴다(이미 걸려 있던 규칙의 값만 바꾸면 묻지 않는다).
 */
export const introducesInMultiplier = (drafts: ExceptionRuleDraft[], savedRules: ExceptionRateRule[] | undefined): boolean => {
    const savedIn = new Set((savedRules ?? []).filter((rule) => rule.mode === 'inMultiplier').map(ruleTargetOf));
    return drafts.some((draft) => draft.mode === 'inMultiplier' && !savedIn.has(draft.target));
};

/** 배수가 곱하는 대상 — 방식마다 원래 수업료의 이름이 다르다. */
export const multiplierBaseLabel = (mode: ExceptionSettlementMode): string =>
    mode === 'base' ? '수업 정산액' : '구간 수업료';

/** 「시간 상관없이 IN 요율」 방식 이름(선택지). 칸 폭(170px)에 들어가게 짧게. */
export const IN_MULTIPLIER_OPTION_LABEL = '항상 IN 요율의 배수';

/**
 * 방식 선택지.
 * - 이전 서버(legacy)에는 요율 방식이 없어 빼고 보인다(이전 서버 Joi 는 percentage 를 400 으로 거부).
 * - 「시간 상관없이 IN 요율」은 상세 정산 + 형식 3 서버에서만. 지금 행이 이미 그 방식이면(서버 롤백 등) 이름이 보이게 남긴다.
 */
export const exceptionModeOptions = (
    mode: ExceptionSettlementMode,
    format: ExceptionSaveFormat = 'v2',
    currentRuleMode?: ExceptionRateMode,
): { value: ExceptionRateMode; label: string }[] => [
    { value: 'fixed', label: '1회 고정 금액' },
    ...(format !== 'legacy' ? [{ value: 'percentage' as const, label: '1회 요율(%)' }] : []),
    { value: 'multiplier', label: `${multiplierBaseLabel(mode)}의 배수` },
    ...((mode === 'tier' && format === 'v3') || currentRuleMode === 'inMultiplier'
        ? [{ value: 'inMultiplier' as const, label: IN_MULTIPLIER_OPTION_LABEL }]
        : []),
];

/** 「시간 상관없이 IN 요율」 값 표기 — 1 이면 곱하지 않으므로 배수를 쓰지 않는다(서버도 1 이면 곱하지 않는다). */
export const formatInMultiplierValue = (value: number): string =>
    value === 1 ? '시간 상관없이 IN 요율' : `시간 상관없이 IN 요율 ×${Number(value.toFixed(4))}`;

const formatWon = (value: number) => `${value.toLocaleString('ko-KR')}원`;
const formatPercent = (value: number) => `${Number(value.toFixed(2))}%`;
const formatShort = (ruleMode: ExceptionRateMode, value: number) =>
    ruleMode === 'fixed' ? formatWon(value) : formatPercent(value);

/**
 * 규칙 한 줄 요약. 예: 「재등록 수강권은 1회 30,000원」, 「공동구매 상품은 구간 수업료 ×1.2」,
 * 기본 정산 「바디프로필 상품은 1회 요율 40% (재등록 45%, 노쇼 10%)」.
 * 재등록 규칙은 「재등록 수강권이면 따로 금액」으로 읽히게 쓴다(10/19 사용자 결정).
 * 값은 저장된 그대로 보인다(화면에서 다시 계산하지 않는다).
 */
export const formatExceptionRule = (
    rule: ExceptionRateRule,
    categoryName: (key: string) => string,
    mode: ExceptionSettlementMode = 'tier',
): string => {
    const target = rule.match.type === 'category' ? `${categoryName(rule.match.key)} 상품은` : '재등록 수강권은';
    let value: string;
    if (rule.mode === 'fixed') value = `1회 ${formatWon(rule.value)}`;
    else if (rule.mode === 'percentage') value = `1회 요율 ${formatPercent(rule.value)}`;
    else if (rule.mode === 'inMultiplier') value = formatInMultiplierValue(rule.value);
    else value = `${multiplierBaseLabel(mode)} ×${Number(rule.value.toFixed(4))}`;
    if (usesThreeValues(mode, rule.mode) && rule.match.type === 'category') {
        const extras: string[] = [];
        const re = finiteOrNull(rule.reRegistrationValue);
        const ns = finiteOrNull(rule.noshowValue);
        if (re !== null) extras.push(`재등록 ${formatShort(rule.mode, re)}`);
        if (ns !== null) extras.push(`노쇼 ${formatShort(rule.mode, ns)}`);
        if (extras.length > 0) value = `${value} (${extras.join(', ')})`;
    }
    return `${target} ${value}`;
};

/**
 * 예외 수업료를 저장하는 달(year·month)이 이번 달보다 이전이면, 그 달부터 지난달까지 이미 계산한 급여가 다시 계산된다는
 * 확인 문구. 이번 달·이후 달이면 null. 기준 「이번 달」은 호출부가 넘긴다(테스트·시간대 고정용).
 */
export const describeRetroactiveExceptionSave = (
    target: { year: string | number; month: string | number },
    now: { year: number; month: number },
): string | null => {
    const y = Number(target.year);
    const m = Number(target.month);
    if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
    if (y > now.year || (y === now.year && m >= now.month)) return null;
    const lastY = now.month === 1 ? now.year - 1 : now.year;
    const lastM = now.month === 1 ? 12 : now.month - 1;
    const label = (yy: number, mm: number) => (yy === now.year ? `${mm}월` : `${yy}년 ${mm}월`);
    const range = y === lastY && m === lastM ? label(y, m) : `${label(y, m)}~${label(lastY, lastM)}`;
    return `${label(y, m)}부터 적용돼요. 이미 계산한 ${range} 급여도 이 금액으로 다시 계산돼요.`;
};

/**
 * 이 정산 방식 탭에서 예외 수업료 입구를 보일지.
 * - exceptionRatesAllModes === true(새 서버): 세 방식 모두
 * - 그 밖(이전 서버): 지금처럼 상세 정산이면서 exceptionRatesEffective === true 일 때만
 */
export const isExceptionRatesVisibleInMode = (
    response: { exceptionRatesEffective?: boolean; exceptionRatesAllModes?: boolean } | null | undefined,
    mode: ExceptionSettlementMode,
): boolean => {
    if (!response) return false;
    if (response.exceptionRatesAllModes === true) return true;
    return mode === 'tier' && response.exceptionRatesEffective === true;
};

/**
 * 저장 본문.
 * - 새 서버(exceptionRatesAllModes): 형식 표식(exceptionRatesFormat: 2)을 **항상** 싣는다(빈 배열 저장 포함).
 *   표식이 없으면 서버는 구형 화면의 저장으로 보고, 새 값(요율·재등록·노쇼)을 지울 수 있으면 409 로 막는다.
 * - 이전 서버(legacy): 예전 형식 그대로 — 표식도, 재등록·노쇼 키도 싣지 않는다. 이전 서버 Joi 는 모르는 키를 400 으로 거부해
 *   기존 상세 정산 예외 저장까지 막히기 때문이다. 이전 서버에는 요율 방식이 없으므로 화면이 고르게 두지 않는다.
 */
export const toPutCoachExceptionRatesRequest = (
    body: PutCoachExceptionRatesBody,
    format: ExceptionSaveFormat = 'v2',
): PutCoachExceptionRatesRequest | PutCoachExceptionRatesBody =>
    format === 'legacy'
        ? {
              coachId: body.coachId,
              year: body.year,
              month: body.month,
              exceptionRates: body.exceptionRates.map((rule) => ({
                  match: rule.match,
                  mode: rule.mode,
                  value: rule.value,
              })),
          }
        : {
              coachId: body.coachId,
              year: body.year,
              month: body.month,
              // 형식 3 = inMultiplier 를 아는 서버. 형식 2 로 inMultiplier 를 보내면 서버가 409 로 막는다.
              exceptionRatesFormat: format === 'v3' ? 3 : 2,
              exceptionRates: body.exceptionRates,
          };

/**
 * 이 서버가 받는 저장 형식 — 요율 조회 응답의 능력 신호로만 판단한다.
 * exceptionRatesInMultiplier → 3, exceptionRatesAllModes → 2, 둘 다 없으면 예전 형식.
 */
export const exceptionSaveFormatOf = (
    response: { exceptionRatesAllModes?: boolean; exceptionRatesInMultiplier?: boolean } | null | undefined,
): ExceptionSaveFormat =>
    response?.exceptionRatesInMultiplier === true
        ? 'v3'
        : response?.exceptionRatesAllModes === true
          ? 'v2'
          : 'legacy';
